import {
  Box,
  Grid,
  Typography,
  Link,
  CircularProgress,
  Paper,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

//... Context & Services
import { AppDataContext } from "../../common/appContext";
import { UserDataService } from "../../services";
import { LeaderDashboardService } from "../../services/leaderDashboard.Service";

//... Configs
import RouteConstants from "../../common/RouteConstants";
import {
  anthrosStations,
  shootingStations,
  strengthAndAgilityStations,
} from "../../configs/AppSettings";

//... Icons
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

//... Shared components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "../../sharedComponents/Accordion/Accordion";

//... Local components
import DashboardCard from "./DashboardCard";
import LoadingCard from "../../sharedComponents/loader/LoadingCard";

const useStyles = makeStyles((theme) => ({
  headerText: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  headerTextInfo: {
    color: "rgb(29, 66, 138)",
    fontSize: "14px",
  },
  headerBox: {
    background: "#c1d0ed",
    borderRadius: "0",
    margin: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      "& > div": {
        flexDirection: "column",
        "& > div": {
          padding: theme.spacing(0),
        },
      },
    },
    "& .MuiAccordionSummary-content": {
      margin: 0,
    },
  },
  selectedEvent: {
    borderBottom: "10px solid #c91330",
    boxShadow: "-1px -1px 10px #000 !important",
    maxWidth: "345px",
  },
  viewAllButton: {
    color: "rgb(29, 66, 138)",
    textTransform: "none",
    marginRight: "15px",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",

    fontWeight: 600,
    textDecoration: "underline",
    letterSpacing: "0.3px",

    transition: "all 0.2s ease",

    "&:hover": {
      color: "#1a3f7a",
      textDecoration: "underline",
      transform: "translateX(2px)",
    },

    "&:active": {
      transform: "translateX(1px)",
    },

    "&:focus": {
      outline: "none",
      textDecoration: "underline",
    },
  },
}));

export default function LeaderBoard() {
  let navigate = useNavigate();
  const classes = useStyles();

  //... Extracted from AppDataContext
  const {
    user,
    selectedEvent,
    selectedEventId,
    setSelectedCombineResultsTab,
    lengthUnits,
    weightUnits,
    getWeightUnitValue,
    getLengthUnitValue,
    getLengthUnitLabelType,
    getWeightUnitLabelType,
    getHandDimensionsUnitValue,
    getHandDimensionsUnitLabelType,
    isShowDEXABodyFat,
  } = useContext(AppDataContext);

  //.. Local state
  const [eventId, setEventId] = useState(selectedEventId);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);

  //... Anthros station state holders
  const [anthroStations, setAnthroStations] = useState({
    bodyFat: { active: false, data: [] },
    dXABodyFat: { active: false, data: [] },
    handDimentions: { active: false, data: [] },
    weight: { active: false, data: [] },
    heightWithShoes: { active: false, data: [] },
    heightWithoutShoes: { active: false, data: [] },
    standingReach: { active: false, data: [] },
    wingSpanDimentions: { active: false, data: [] },
  });

  //... Strength And Agility station state holders
  const [sasStations, setSasStations] = useState({
    laneShuttleLeft: { active: false, data: [] },
    laneShuttleRight: { active: false, data: [] },
    laneShuttleLeftLaser: { active: false, data: [] },
    laneShuttleRightLaser: { active: false, data: [] },
    threeFour: { active: false, data: [] },
    proLane: { active: false, data: [] },
    threeFourLaser: { active: false, data: [] },
    proLaneLaser: { active: false, data: [] },
    maxVerticalJump: { active: false, data: [] },
    standingVerticalJump: { active: false, data: [] },
  });

  //... Shooting station state holders
  const [shootingStationsState, setShootingStationsState] = useState({
    spotUp: { active: false, data: [] },
    sideMidSide: { active: false, data: [] },
    offTheDribble: { active: false, data: [] },
    freeThrows: { active: false, data: [] },
    point3Star: { active: false, data: [] },
    midRangeStar: { active: false, data: [] },
    midSideMid: { active: false, data: [] },
  });

  //... Expand state for accordions
  const [expanded, setExpanded] = useState({
    anthros: true,
    strengthAndAgility: true,
    laserTime: true,
    shootingDrills: true,
  });

  //... UseEffects
  useEffect(() => {
    if (sessionStorage.getItem("checkUserAndNavigation") === "true") {
      UserDataService.redirectToTeamDashboard().then((bRedirect) => {
        if (bRedirect) navigate(RouteConstants.TEAM_BALLOT_DASHBOARD);
      });
    }
  }, []);

  useEffect(() => {
    setEventId(selectedEventId);
    if (selectedEventId) {
      setEventId(selectedEventId);
    }
  }, [selectedEventId]);

  useEffect(() => {
    if (!user?.loginID) return;

    GetLeaderBoardNotes();
  }, [user?.loginID]);

  useEffect(() => {
    if (eventId === 0 || !user?.loginID) return;

    const fetchLeaderboardData = async () => {
      try {
        setLoading(true);
        await GetLeaderBoardData();
      } catch (error) {
        console.error("Error fetching leaderboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboardData();
  }, [eventId, user?.loginID]);

  //...Handlers and functions
  const updateAnthroStation = (key, payload) => {
    setAnthroStations((prev) => ({ ...prev, [key]: payload }));
  };

  const updateSasStation = (key, payload) => {
    setSasStations((prev) => ({ ...prev, [key]: payload }));
  };

  const updateShootingStation = (key, payload) => {
    setShootingStationsState((prev) => ({ ...prev, [key]: payload }));
  };

  const handleToggle = (key) => {
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  //... Service calls & related functions
  const GetLeaderBoardData = async () => {
    try {
      const [{ data: flags }, anthrosResponse, sasResponse, shootingResponse] =
        await Promise.all([
          LeaderDashboardService.getStrengthAndAgilityFlag(),
          LeaderDashboardService.getAnthrosConsolidatedLeaders(eventId),
          LeaderDashboardService.getSASConsolidatedLeaders(eventId),
          LeaderDashboardService.getShootingConsolidatedLeaders(eventId),
        ]);

      if (!flags?.length) return;

      const anthrosMap = {
        101: "bodyFat",
        102: "handDimentions",
        103: "weight",
        104: "heightWithShoes",
        105: "heightWithoutShoes",
        106: "standingReach",
        107: "wingSpanDimentions",
        108: "dXABodyFat",
      };

      const shootingMap = {
        301: "spotUp",
        302: "sideMidSide",
        303: "offTheDribble",
        304: "freeThrows",
        305: "point3Star",
        306: "midRangeStar",
        307: "midSideMid",
      };

      const sasMap = {
        "1_201": { key: "laneShuttleLeft", positionId: 0 },
        "2_201": { key: "laneShuttleRight", positionId: 1 },
        "20_201": { key: "laneShuttleRightLaser", positionId: 2 },
        "21_201": { key: "laneShuttleLeftLaser", positionId: 3 },
        "22_202": { key: "threeFourLaser", positionId: 1 },
        "23_203": { key: "proLaneLaser", positionId: 1 },

        // no positionId cases
        202: { key: "threeFour", positionId: null },
        203: { key: "proLane", positionId: null },
        204: { key: "maxVerticalJump", positionId: null },
        205: { key: "standingVerticalJump", positionId: null },
      };

      flags.forEach((flag) => {
        let stationData;

        //... Anthros
        if (flag.childID > 6 && anthrosStations.includes(flag.childName)) {
          stationData = anthrosResponse.data.filter(
            (item) => String(item.stationId) === flag.childName
          );
          const key = anthrosMap[flag.childName];
          key &&
            updateAnthroStation(key, {
              active: flag.isActive,
              data: stationData,
            });
        }

        //... Shooting
        else if (
          flag.childID > 13 &&
          shootingStations.includes(flag.childName)
        ) {
          stationData = shootingResponse.data.filter(
            (item) => String(item.stationId) === flag.childName
          );
          const key = shootingMap[flag.childName];
          key &&
            updateShootingStation(key, {
              active: flag.isActive,
              data: stationData,
            });
        }

        //... SAS / Strength & Agility
        else if (strengthAndAgilityStations.includes(flag.childName)) {
          const stationData = sasResponse.data;

          const mapKey =
            sasMap[`${flag.childID}_${flag.childName}`] ||
            sasMap[String(flag.childName)];

          if (mapKey) {
            const { key, positionId } = mapKey;

            const filteredData = stationData.filter(
              ({ stationId, positionId: pos }) =>
                String(stationId) === String(flag.childName) &&
                pos === positionId
            );

            updateSasStation(key, {
              active: flag.isActive,
              data: filteredData,
            });
          }
        }
      });
    } catch (error) {
      console.error("Error fetching leaderboard data:", error);
    }
  };

  const GetLeaderBoardNotes = async () => {
    try {
      const { data } = await LeaderDashboardService.getLeaderBoardNotes();

      const uniqueNotes = [
        ...new Map(
          data
            .filter((note) => note.childID === parseInt(selectedEventId))
            .map((note) => [note.childName, note])
        ).values(),
      ];

      setNotes(uniqueNotes);
    } catch (error) {
      console.error("Error fetching leaderboard notes:", error);
    }
  };

  const navigateToCombineResultsTab = (selectedTabIndex) => {
    setSelectedCombineResultsTab(selectedTabIndex);
    navigate(`${eventId}${RouteConstants.COMBINE_RESULTS}`);
  };

  function getSpecifiedNotes(iStationID) {
    switch (iStationID) {
      case 1:
        return notes.find(
          (x) =>
            x.childName.replace(/\s/g, "").toLowerCase() ===
            "StrengthAndAgility".toLowerCase()
        );
      case 2:
        return notes.find(
          (x) =>
            x.childName.replace(/\s/g, "").toLowerCase() ===
            "Shooting".toLowerCase()
        );
      default:
        return notes.find(
          (x) =>
            x.childName.replace(/\s/g, "").toLowerCase() ===
            "Anthropometrics".toLowerCase()
        );
    }
  }

  function getNotesBody(iStationID) {
    const notesDetails = getSpecifiedNotes(iStationID);

    return (
      notesDetails?.isActive && (
        <>
          {" "}
          <Box display="flex" pl={2} pt={2}>
            <InfoOutlinedIcon
              style={{
                color: "#1d428a",
                paddingRight: "5px",
                marginLeft: "5px",
              }}
            />
            <Typography
              variant="caption"
              className={classes.headerText}
              sx={{ fontSize: "14px" }}
            >
              {notesDetails.description}
            </Typography>
          </Box>
        </>
      )
    );
  }

  return (
    <Box mt={2} mb={2}>
      {/* Anthropometrics */}
      <Box sx={{ p: 1, m: 1 }}>
        <Accordion
          expanded={expanded.anthros}
          onChange={() => handleToggle("anthros")}
        >
          <AccordionSummary
            aria-controls="panel2d-content"
            id="panel2d-header"
            expanded={expanded.anthros}
            className={classes.headerBox}
          >
            <Box
              display="flex"
              alignItems="center"
              flexDirection="row"
              justifyContent="space-between"
              sx={{ width: "100%" }}
            >
              <Box display="flex" pl={2}>
                <Typography className={`${classes.headerText} headerTitle`}>
                  Anthros Leader Boards
                </Typography>

                {getNotesBody(0)}
              </Box>
              <Box>
                <Link
                  variant="subtitle1"
                  fontWeight="medium"
                  underline="none"
                  className={classes.viewAllButton}
                  onClick={() => navigateToCombineResultsTab(0)}
                >
                  {" "}
                  View All
                </Link>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {loading ? (
              <Box display="flex" justifyContent="center">
                <LoadingCard />
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ p: 2 }}>
                <DashboardCard
                  title="Standing Reach"
                  data={anthroStations.standingReach}
                  unit={getLengthUnitLabelType(lengthUnits)}
                  value="106"
                  from="HomeContainer"
                  eventId={eventId}
                  getLengthUnitValue={getLengthUnitValue}
                />

                <DashboardCard
                  title="Wingspan Dimensions"
                  data={anthroStations.wingSpanDimentions}
                  unit={getLengthUnitLabelType(lengthUnits)}
                  value="107"
                  from="HomeContainer"
                  eventId={eventId}
                  getLengthUnitValue={getLengthUnitValue}
                />

                {/* Note:Commenting code client requirement */}
                {/* <DashboardCard
              title="Height With Shoes"
              unit={getLengthUnitLabelType(lengthUnits)}
              data={anthroStations.heightWithShoes}
              value="104"
              from="HomeContainer"
              eventId={eventId}
              getLengthUnitValue={getLengthUnitValue}
            /> */}

                <DashboardCard
                  title="Height W/o Shoes"
                  data={anthroStations.heightWithoutShoes}
                  unit={getLengthUnitLabelType(lengthUnits)}
                  value="105"
                  from="HomeContainer"
                  eventId={eventId}
                  getLengthUnitValue={getLengthUnitValue}
                />

                <DashboardCard
                  title="Weight"
                  unit={getWeightUnitLabelType(weightUnits)}
                  data={anthroStations.weight}
                  value="103"
                  from="HomeContainer"
                  eventId={eventId}
                  getWeightUnitValue={getWeightUnitValue}
                />

                <DashboardCard
                  title="Hand Dimensions"
                  data={anthroStations.handDimentions}
                  unit={`W ${getHandDimensionsUnitLabelType(
                    lengthUnits
                  )} | L ${getHandDimensionsUnitLabelType(lengthUnits)}`}
                  value="102"
                  from="HomeContainer"
                  eventId={eventId}
                  getHandDimensionsUnitValue={getHandDimensionsUnitValue}
                />

                {/* <DashboardCard
                title="Body Fat"
                unit="(%)"
                data={anthroStations.bodyFat}
                value="101"
                from="HomeContainer"
                eventId={eventId}
              />

              {isShowDEXABodyFat === "true" && (
                <DashboardCard
                  title="DEXA Body Fat"
                  unit="(%)"
                  data={anthroStations.dXABodyFat}
                  value="108"
                  from="HomeContainer"
                  eventId={eventId}
                />
              )} */}
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Strength and Agility */}
      <Box sx={{ p: 1, m: 1 }}>
        <Accordion
          expanded={expanded.strengthAndAgility}
          onChange={() => handleToggle("strengthAndAgility")}
        >
          <AccordionSummary
            aria-controls="panel2d-content"
            id="panel2d-header"
            expanded={expanded.strengthAndAgility}
            className={classes.headerBox}
          >
            <Box
              display="flex"
              alignItems="center"
              flexDirection="row"
              justifyContent="space-between"
              sx={{ width: "100%" }}
            >
              <Box display="flex" pl={2}>
                <Typography className={`${classes.headerText} headerTitle`}>
                  Strength And Agility
                </Typography>
                {getNotesBody(1)}{" "}
                <Box display="flex" pl={1} pt={2}>
                  <Typography
                    variant="caption"
                    className={classes.headerText}
                    sx={{ fontSize: "14px" }}
                  >
                    {"(Hand time)"}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Link
                  variant="subtitle1"
                  fontWeight="medium"
                  underline="none"
                  className={classes.viewAllButton}
                  onClick={() => navigateToCombineResultsTab(1)}
                >
                  {" "}
                  View All
                </Link>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {loading ? (
              <Box display="flex" justifyContent="center">
                <LoadingCard />
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ p: 2 }}>
                {/* <DashboardCard
              title="Lane Shuttle Left(s)"
              data={sasStations.laneShuttleLeft}
              value="2010"
              unit="(sec)"
              from="HomeContainer"
              eventId={eventId}
            /> */}

                {/* <DashboardCard
              title="Lane Shuttle Right(s)"
              data={sasStations.laneShuttleRight}
              value="2011"
              unit="(sec)"
              from="HomeContainer"
              eventId={eventId}
            /> */}

                <DashboardCard
                  title="3/4 Court Sprint (s)"
                  data={sasStations.threeFour}
                  value="202"
                  unit="(sec)"
                  from="HomeContainer"
                  eventId={eventId}
                />

                <DashboardCard
                  title="Pro Lane(s)"
                  unit="(sec)"
                  data={sasStations.proLane}
                  value="203"
                  from="HomeContainer"
                  eventId={eventId}
                />

                <DashboardCard
                  title="Max Vertical Jump"
                  data={sasStations.maxVerticalJump}
                  unit={getHandDimensionsUnitLabelType(lengthUnits)}
                  value="204"
                  from="HomeContainer"
                  eventId={eventId}
                  getHandDimensionsUnitValue={getHandDimensionsUnitValue}
                />

                <DashboardCard
                  title="Standing Vertical"
                  data={sasStations.standingVerticalJump}
                  unit={getHandDimensionsUnitLabelType(lengthUnits)}
                  value="205"
                  from="HomeContainer"
                  eventId={eventId}
                  getHandDimensionsUnitValue={getHandDimensionsUnitValue}
                />
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Strength and Agility Laser Time */}
      <Box sx={{ p: 1, m: 1 }}>
        <Accordion
          expanded={expanded.laserTime}
          onChange={() => handleToggle("laserTime")}
        >
          <AccordionSummary
            aria-controls="panel2d-content"
            id="panel2d-header"
            expanded={expanded.laserTime}
            className={classes.headerBox}
          >
            <Box
              display="flex"
              alignItems="center"
              flexDirection="row"
              justifyContent="space-between"
              sx={{ width: "100%" }}
            >
              <Box display="flex" pl={2}>
                <Typography className={`${classes.headerText} headerTitle`}>
                  Strength And Agility Laser Time
                </Typography>
                {getNotesBody(1)}
              </Box>
              <Box>
                <Link
                  variant="subtitle1"
                  fontWeight="medium"
                  underline="none"
                  className={classes.viewAllButton}
                  onClick={() => navigateToCombineResultsTab(1)}
                >
                  {" "}
                  View All
                </Link>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {loading ? (
              <Box display="flex" justifyContent="center">
                <LoadingCard />
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ p: 2 }}>
                <DashboardCard
                  title="Lane Shuttle Left(s)"
                  data={sasStations.laneShuttleLeftLaser}
                  value="20121"
                  unit="(sec)"
                  from="HomeContainer"
                  eventId={eventId}
                />

                <DashboardCard
                  title="Lane Shuttle Right(s)"
                  data={sasStations.laneShuttleRightLaser}
                  value="20120"
                  unit="(sec)"
                  from="HomeContainer"
                  eventId={eventId}
                />

                <DashboardCard
                  title="3/4 Court Sprint (s)"
                  data={sasStations.threeFourLaser}
                  value="20222"
                  unit="(sec)"
                  from="HomeContainer"
                  eventId={eventId}
                />

                <DashboardCard
                  title="Pro Lane(s)"
                  unit="(sec)"
                  data={sasStations.proLaneLaser}
                  value="20323"
                  from="HomeContainer"
                  eventId={eventId}
                />
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Shooting Drills */}
      <Box sx={{ p: 1, m: 1 }}>
        <Accordion
          expanded={expanded.shootingDrills}
          onChange={() => handleToggle("shootingDrills")}
        >
          <AccordionSummary
            aria-controls="panel2d-content"
            id="panel2d-header"
            expanded={expanded.shootingDrills}
            className={classes.headerBox}
          >
            <Box
              display="flex"
              alignItems="center"
              flexDirection="row"
              justifyContent="space-between"
              sx={{ width: "100%" }}
            >
              <Box display="flex" pl={2}>
                <Typography className={`${classes.headerText} headerTitle`}>
                  Shooting Drills
                </Typography>
                {getNotesBody(2)}
              </Box>
              <Box>
                <Link
                  variant="subtitle1"
                  fontWeight="medium"
                  underline="none"
                  className={classes.viewAllButton}
                  onClick={() => navigateToCombineResultsTab(2)}
                >
                  {" "}
                  View All
                </Link>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {loading ? (
              <Box display="flex" justifyContent="center">
                <LoadingCard />
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ p: 2 }}>
                <DashboardCard
                  title="Spot Up"
                  data={shootingStationsState.spotUp}
                  value="301"
                  from="HomeContainer"
                  eventId={eventId}
                />

                <DashboardCard
                  title="Side Mid Side"
                  data={shootingStationsState.sideMidSide}
                  value="302"
                  from="HomeContainer"
                  eventId={eventId}
                />

                <DashboardCard
                  title="Mid Side Mid"
                  data={shootingStationsState.midSideMid}
                  value="307"
                  from="HomeContainer"
                  eventId={eventId}
                />

                <DashboardCard
                  title="Off The Dribble"
                  data={shootingStationsState.offTheDribble}
                  value="303"
                  from="HomeContainer"
                  eventId={eventId}
                />

                <DashboardCard
                  title="Free Throws"
                  data={shootingStationsState.freeThrows}
                  value="304"
                  from="HomeContainer"
                  eventId={eventId}
                />

                <DashboardCard
                  title="3-Point Star"
                  data={shootingStationsState.point3Star}
                  value="305"
                  from="HomeContainer"
                  eventId={eventId}
                />

                <DashboardCard
                  title="Mid Range Star"
                  data={shootingStationsState.midRangeStar}
                  value="306"
                  from="HomeContainer"
                  eventId={eventId}
                />
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
}
